import React, { Fragment, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import i18next from 'i18next';
import TerminalError from '../shared/TerminalError';
import WebpcscError from '../shared/WebpcscError';
import { WEBPCSC_ERROR, homePath } from '../../constants';
import { StyledNavIcon, AccentButton } from '../../styles/common';

// Inner class component that handles error boundary logic
// Must remain a class component because componentDidCatch only works in class components
class ErrorBoundaryInner extends React.Component {
    constructor(props) {
        super(props);
        this.handleEnter = this.handleEnter.bind(this);
    }

    state = {
        hasError: false,
        errorMsg: '',
    };

    handleEnter(e) {
        const { navigate, pathname } = this.props;
        if (e.keyCode === 13 && (this.state.hasError || this.props.hasError)) {
            navigate(pathname || homePath);
        }
    }

    componentDidMount() {
        document.addEventListener('keyup', this.handleEnter);
    }

    componentWillUnmount() {
        document.removeEventListener('keyup', this.handleEnter);
    }

    // Called by parent wrapper when location changes
    handleLocationChange() {
        if (this.state.hasError || this.props.hasError) {
            this.setState({
                hasError: false,
                errorMsg: '',
            });
            this.props.appDispatch({ type: 'CLEAR_ERROR' });
        }
    }

    componentDidCatch(error, info) {
        console.error('ErrorBoundary componentDidCatch error:', error);
        console.error('ErrorBoundary componentDidCatch info:', info);
        this.setState({
            hasError: true,
            errorMsg: i18next.t(`errorMsgs$${error.message}`),
        });
    }

    handleOkClick = () => {
        const { navigate, pathname, appDispatch } = this.props;
        this.setState({
            hasError: false,
            errorMsg: '',
        });
        appDispatch({ type: 'CLEAR_ERROR' });
        navigate(pathname || homePath);
    };

    displayError = () => {
        const { errorType, errorMsg } = this.props;
        let Cmp = TerminalError;
        if (errorType === WEBPCSC_ERROR) {
            Cmp = WebpcscError;
        }
        return (
            <Cmp error={this.state.hasError ? this.state.errorMsg : errorMsg}>
                <Row>
                    <Col sm="12">
                        <AccentButton outline size="sm" onClick={this.handleOkClick}>
                            <StyledNavIcon icon="check" />
                            {i18next.t(
                                'terminusCardLabels$stateless$errorOk',
                            )}
                        </AccentButton>
                    </Col>
                </Row>
            </Cmp>
        );
    };

    render() {
        const hasError =
            this.state.hasError === true || this.props.hasError === true;
        return (
            <Fragment>
                {hasError ? this.displayError() : this.props.children}
            </Fragment>
        );
    }
}

// Wrapper functional component that provides router hooks to the class component
const ErrorBoundary = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const boundaryRef = useRef(null);
    const prevPathRef = useRef(location.pathname);

    // Replace history.listen() with useEffect watching location changes
    useEffect(() => {
        if (prevPathRef.current !== location.pathname) {
            prevPathRef.current = location.pathname;
            if (boundaryRef.current) {
                boundaryRef.current.handleLocationChange();
            }
        }
    }, [location.pathname]);

    return (
        <ErrorBoundaryInner
            ref={boundaryRef}
            navigate={navigate}
            pathname={location.pathname}
            {...props}
        />
    );
};

export default ErrorBoundary;
